import React from 'react';
import { Chip } from '@mui/material';
import { RESOURCES } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import ConfigSection from '../ConfigSection';
import { AccountPicker } from '../LedgerPickers';

// Asset categories: the depreciation policy per asset class (verified
// against CreateAssetCategoryDto). method is STRAIGHT_LINE /
// DECLINING_BALANCE / NONE; the three GL legs (cost, accumulated
// depreciation, expense) are all required. Only name, description, life,
// rate and the active flag are editable after creation.
const depreciationMethods = [
  { label: 'Straight line', value: 'STRAIGHT_LINE' },
  { label: 'Declining balance', value: 'DECLINING_BALANCE' },
  { label: 'No depreciation', value: 'NONE' },
];

const descriptor = {
  resource: RESOURCES.LEDGER.ASSETS,
  title: 'Asset Categories',
  subtitle: 'Depreciation policy per asset class, with its GL postings',
  idKey: 'category_id',
  searchPlaceholder: 'Search categories...',
  columns: [
    { label: 'Code', render: (c) => <strong>{c.category_code}</strong> },
    { label: 'Name', render: (c) => c.category_name },
    { label: 'Method', render: (c) => (c.method || '').replace(/_/g, ' ') || '-' },
    {
      label: 'Rate / Life',
      render: (c) =>
        c.depreciation_rate != null
          ? `${c.depreciation_rate}% p.a.`
          : c.useful_life_months != null
            ? `${Math.round((Number(c.useful_life_months) / 12) * 10) / 10} yrs`
            : '-',
    },
    {
      label: 'Status',
      render: (c) => (
        <Chip label={c.is_active === false ? 'Inactive' : 'Active'} color={c.is_active === false ? 'default' : 'success'} size="small" />
      ),
    },
  ],
  fields: [
    { key: 'category_code', label: 'Code', required: true, helper: '2-20 chars, e.g. VEH - locked after creation', md: 6 },
    { key: 'category_name', label: 'Category Name', required: true, md: 6 },
    { key: 'method', label: 'Depreciation Method', type: 'select', options: depreciationMethods, required: true, helper: 'Locked after creation', md: 6 },
    { key: 'useful_life_months', label: 'Useful Life (months)', type: 'number', min: 1, md: 6 },
    { key: 'depreciation_rate', label: 'Annual Rate % (declining balance)', type: 'number', min: 0, md: 6 },
    { key: 'cost_account_id', label: 'Cost Account (BS)', type: 'picker', picker: AccountPicker, required: true, helper: 'Locked after creation', md: 6 },
    { key: 'accum_depr_account_id', label: 'Accumulated Depreciation Account', type: 'picker', picker: AccountPicker, required: true, helper: 'Locked after creation', md: 6 },
    { key: 'expense_account_id', label: 'Depreciation Expense Account (P&L)', type: 'picker', picker: AccountPicker, required: true, helper: 'Locked after creation', md: 6 },
    { key: 'description', label: 'Description', type: 'textarea', md: 12 },
    { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
  ],
  initialValues: {
    category_code: '', category_name: '', method: 'STRAIGHT_LINE',
    useful_life_months: '', depreciation_rate: '',
    cost_account_id: '', accum_depr_account_id: '', expense_account_id: '',
    description: '', is_active: true,
  },
  fetchList: (params) => ledgerAPI.getAssetCategories(params),
  createItem: (payload) => ledgerAPI.createAssetCategory(payload),
  updateItem: (id, payload) => ledgerAPI.updateAssetCategory(id, payload),
  deleteItem: (id) => ledgerAPI.deleteAssetCategory(id),
  deleteMessage: (item) => `Delete asset category ${item.category_name}? Categories with ACTIVE/DISPOSED/RETIRED assets are protected.`,
  mapToForm: (item) => ({
    category_code: item.category_code || '',
    category_name: item.category_name || '',
    method: item.method || 'STRAIGHT_LINE',
    useful_life_months: item.useful_life_months ?? '',
    depreciation_rate: item.depreciation_rate ?? '',
    cost_account_id: item.cost_account_id ?? '',
    accum_depr_account_id: item.accum_depr_account_id ?? '',
    expense_account_id: item.expense_account_id ?? '',
    description: item.description || '',
    is_active: item.is_active !== false,
  }),
  mapToPayload: (values, isEdit) => {
    if (isEdit) {
      const payload = {};
      if (values.category_name !== undefined) payload.category_name = values.category_name;
      if (values.description !== undefined) payload.description = values.description;
      if (values.useful_life_months !== '' && values.useful_life_months !== undefined) payload.useful_life_months = Number(values.useful_life_months);
      if (values.depreciation_rate !== '' && values.depreciation_rate !== undefined) payload.depreciation_rate = Number(values.depreciation_rate);
      if (values.is_active !== undefined) payload.is_active = values.is_active;
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === undefined) delete payload[k];
      });
      return payload;
    }
    const out = { ...values };
    Object.keys(out).forEach((k) => {
      if (out[k] === '' || out[k] === undefined) delete out[k];
    });
    if (out.useful_life_months !== undefined) out.useful_life_months = Number(out.useful_life_months);
    if (out.depreciation_rate !== undefined) out.depreciation_rate = Number(out.depreciation_rate);
    ['cost_account_id', 'accum_depr_account_id', 'expense_account_id'].forEach((k) => {
      if (out[k] !== undefined) out[k] = Number(out[k]);
    });
    return out;
  },
  // ConfigSection calls validate(values) in both modes; mapToForm repopulates
  // every field on edit, so create-rules hold there too, while
  // mapToPayload strips immutable fields before PATCH.
  validate: (values) => {
    if (!String(values.category_code ?? '').trim()) return 'category code is required';
    if (!String(values.category_name ?? '').trim()) return 'category name is required';
    if (!values.method) return 'depreciation method is required';
    for (const key of ['cost_account_id', 'accum_depr_account_id', 'expense_account_id']) {
      if (!values[key]) return `${key.replace(/_/g, ' ')} is required`;
    }
    return null;
  },
};

const AssetCategoriesTab = () => <ConfigSection descriptor={descriptor} />;

export default AssetCategoriesTab;
